import { ReactNode } from 'react'
import { Flex, FlexProps, Box } from 'theme-ui'
import { Chip, ChipProps, Text } from 'components'
import { noop } from 'utils'
import { UserType } from 'types'
import moment from 'moment'
import { STUDENT_DC, STUDENT_INPERSON } from '../../constants'

interface SearchResultsProps {
  limit?: number
  results?: any[]
  show?: boolean
  noResults?: boolean
  isLoading?: boolean
  LoadingComponent?: ReactNode
  onSearchItemClick?: (id: string) => void
  sx?: {}
}

type SearchItemProps = FlexProps & {
  name: string
  email: string
  userType: UserType
  instructionDate: string
  status: ChipProps['variant']
}

function SearchItem({
  name,
  email,
  instructionDate,
  status,
  userType,
  ...rest
}: SearchItemProps) {
  const type =
    userType === STUDENT_DC
      ? 'Digital'
      : userType === STUDENT_INPERSON
      ? 'In person'
      : 'Teacher'
  return (
    <Flex
      variant="layout.result.userSearch"
      data-cy="search-item"
      sx={{
        '&:hover': {
          bg: '#fff7e2',
        },
      }}
      {...rest}>
      <Box sx={{ flex: ['0 0 100%', '1 1 20%'], textAlign: 'left' }}>
        <Text variant="text.result" sx={{ pl: '2px', fontWeight: 'heading' }}>
          {name}
        </Text>
        <Text
          variant="text.informational"
          sx={{
            display: ['inline-box', 'none'],
          }}>
          {email}
        </Text>
      </Box>
      <Box
        sx={{
          flex: ['none', '1 1 35%'],
          textAlign: 'left',
          display: ['none', 'flex'],
        }}>
        <Text
          variant="text.results"
          sx={{
            width: 170,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 2,
          }}>
          {email}
        </Text>
      </Box>
      <Box sx={{ flex: ['0 0 auto', 'none'], display: ['flex', 'none'] }}>
        {type}
      </Box>

      <Box sx={{ flex: ['none', '1 1 25%'], display: ['none', 'flex'] }}>
        <Text variant="text.results">
          {instructionDate
            ? moment(instructionDate).format('MMM DD, YYYY')
            : 'N/A'}
        </Text>
      </Box>
      <Box sx={{ flex: ['none', '1 1 20%'], display: ['none', 'flex'] }}>
        {status ? <Chip sx={{ mb: 'auto' }} variant={status} /> : null}
      </Box>
    </Flex>
  )
}

export function SearchResults({
  show = false,
  results = [],
  limit = 5,
  noResults = false,
  isLoading = false,
  LoadingComponent,
  sx,
  onSearchItemClick = noop,
}: SearchResultsProps) {
  const filteredResults = results.slice(0, limit)

  function handleItemClick({ id }: { id: string }) {
    onSearchItemClick(id)
  }

  const SearchContents = noResults ? (
    <Box sx={{ p: 4, margin: 'auto' }}>
      <Text>No results found.</Text>
    </Box>
  ) : (
    filteredResults.map((student) => (
      <SearchItem
        id={student.id}
        key={student.id}
        name={`${student.forename} ${student.surname}`}
        email={student.email}
        instructionDate={student.instructionDate}
        status={student.status}
        userType={student.userType}
        onClick={() => handleItemClick({ id: student.id })}
      />
    ))
  )

  return (
    <Box
      variant="layout.resultsTray.container"
      sx={{
        width: '100%',
        zIndex: 1000,
        ...sx,
      }}>
      <Flex
        variant="layout.resultsTray.search"
        sx={{
          flexDirection: 'column',
          transform: show ? 'scaley(1)' : 'scaley(0)',
          opacity: show ? 1 : '0',
          visibility: show ? 'visible' : 'hidden',
        }}
        data-cy="result-container">
        {isLoading ? (
          <Box sx={{ p: 4, margin: 'auto' }}>{LoadingComponent}</Box>
        ) : (
          SearchContents
        )}
      </Flex>
    </Box>
  )
}
